import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {ActiveColumnsList} from '../../dashboard/ActiveColumnsList';
import {renderSearchBar} from '../../shared/ColumnsSelectorModalHelpers';
import {FieldHelpIcon} from '../../elements/FieldHelpIcon';
import {hasFieldDescription} from '../../../utils/fieldDescriptions';
import {FieldSearchEmpty} from '../../elements/FieldSearchEmpty';
import {HELP_GA4_CUSTOM_FIELDS, LinkToArticle} from '../../help_center/LinkToArticle';
import {FIELD_KINDS} from '../state/informeModel';
import {fieldsOverflow, layoutFieldsOverflow} from '../state/fieldsLimits';
import {ga4SplitColumns, withoutColumns} from '../state/ga4Split';

/** El grupo «GA4 · Personalizadas» del catálogo (backend: `FIELDS_GROUP_GA4_CUSTOM`). */
export const GA4_CUSTOM_GROUP = 'GA4_CUSTOM';

/**
 * ¿El campo entra en la búsqueda? Por nombre, por id y, en los personalizados de GA4, por el
 * parámetro tal como se llama en GA4 («event_category»): es como lo conoce quien lo definió.
 * Mismo criterio que el Explorador (`DataExplorerColumnsModal.filterFieldsBySearch`).
 */
export function fieldMatchesSearch(field, needle) {
    if (!needle) return true;
    const haystack = [field.name, field.id, field.ga4_custom && field.ga4_custom.parameter];
    return haystack.some((text) => (text || '').toLowerCase().includes(needle));
}

/**
 * Los campos disponibles para la fuente, agrupados por segmento y grupo del catálogo. Los
 * personalizados de GA4 conservan `description` y `ga4_custom`: su ayuda viene en el catálogo (no
 * tienen clave de i18n, son de cada propiedad) y se buscan por el parámetro.
 */
export function availableFieldsByGroup(fieldsData, sourceType, segmentKey) {
    if (!fieldsData || !fieldsData.FIELDS_BY_DATA_SOURCE_TYPE || !fieldsData.FIELD_DATA_BY_ID) return [];
    const {FIELDS_BY_DATA_SOURCE_TYPE, FIELD_DATA_BY_ID, FIELD_SEGMENTS, FIELDS_GROUP_DATA} = fieldsData;
    const ids = FIELDS_BY_DATA_SOURCE_TYPE[sourceType] || [];
    const segments = segmentKey === 'DIMENSIONS'
        ? [FIELD_SEGMENTS.DIMENSIONS]
        : [FIELD_SEGMENTS.METRICS, FIELD_SEGMENTS.INDICATORS].filter(Boolean);
    const byGroup = {};
    ids.forEach((id) => {
        const field = FIELD_DATA_BY_ID[id];
        if (!field || !segments.includes(field.segment)) return;
        const group = field.group || '_';
        if (!byGroup[group]) byGroup[group] = [];
        byGroup[group].push({id, name: field.name || id, description: field.description || null, ga4_custom: field.ga4_custom || null});
    });
    const groupName = (groupId) => (FIELDS_GROUP_DATA && FIELDS_GROUP_DATA[groupId] && FIELDS_GROUP_DATA[groupId].name) || '';
    return Object.keys(byGroup).map((groupId) => ({id: groupId, name: groupName(groupId), fields: byGroup[groupId]}));
}

export function kindOf(fieldsData, id) {
    const field = fieldsData && fieldsData.FIELD_DATA_BY_ID && fieldsData.FIELD_DATA_BY_ID[id];
    const segments = fieldsData && fieldsData.FIELD_SEGMENTS;
    return field && segments && field.segment === segments.DIMENSIONS ? FIELD_KINDS.DIMENSION : FIELD_KINDS.METRIC;
}

/** La clave de un grupo plegable: el mismo id de grupo puede estar en los dos segmentos. */
export const groupKey = (segmentKey, groupId) => `${segmentKey}:${groupId}`;

/**
 * Los grupos que arrancan DESPLEGADOS: los que tienen algún campo elegido (E1, Morten). Con todos
 * abiertos el modal eran cientos de renglones, y lo elegido quedaba enterrado entre grupos que no
 * se usan.
 */
export function initialOpenGroups(groupsBySegment, activeIds) {
    const chosen = new Set(activeIds || []);
    const open = new Set();
    Object.keys(groupsBySegment).forEach((segmentKey) => {
        (groupsBySegment[segmentKey] || []).forEach((group) => {
            if (group.fields.some((field) => chosen.has(field.id))) open.add(groupKey(segmentKey, group.id));
        });
    });
    return open;
}

/**
 * El orden nuevo de la lista elegida tras arrastrar DENTRO de un tipo: primero las dimensiones,
 * después las métricas, que es como se guardan (`informeReducer`, `dimensionNames`/`metricNames`).
 * Una métrica soltada entre dos dimensiones perdía su lugar sin aviso (E12): por eso cada tipo es
 * su propia lista, y `next` tiene que ser una permutación de los campos de ESE tipo — si trae uno
 * del otro, no se toca nada.
 */
export function reorderWithinKind(active, fieldsData, kind, next) {
    const ofKind = active.filter((id) => kindOf(fieldsData, id) === kind);
    const isPermutation = next.length === ofKind.length && next.every((id) => ofKind.includes(id));
    if (!isPermutation) return active;
    const dimensions = kind === FIELD_KINDS.DIMENSION ? next : active.filter((id) => kindOf(fieldsData, id) === FIELD_KINDS.DIMENSION);
    const metrics = kind === FIELD_KINDS.METRIC ? next : active.filter((id) => kindOf(fieldsData, id) === FIELD_KINDS.METRIC);
    return [...dimensions, ...metrics];
}

/** La bajada de la paleta: la de siempre, o la de «+ Agregar otra…» desde un gráfico (C-05). */
function addingLead(t, addingKind) {
    if (addingKind === FIELD_KINDS.METRIC) {
        return t('informes.fields.hint_add_metric', 'Marca la métrica que quieres sumar y aplica: entra a «Métricas y dimensiones» y vuelve elegida en el gráfico.');
    }
    if (addingKind === FIELD_KINDS.DIMENSION) {
        return t('informes.fields.hint_add_dimension', 'Marca la dimensión que quieres sumar y aplica: entra a «Métricas y dimensiones» y vuelve elegida en el gráfico.');
    }
    return t('informes.fields.hint', 'Los campos del informe: definen sobre qué pueden elegir los gráficos y qué columnas puede mostrar cada tabla. Se guardan en la plantilla, no en el informe.');
}

/** «Plataforma, Cuenta y Estado de campaña»: la lista con la conjunción del idioma. */
function listOfNames(names, language) {
    try {
        return new Intl.ListFormat(language || 'es', {style: 'long', type: 'conjunction'}).format(names);
    } catch (e) {
        return names.join(', ');
    }
}

/**
 * «Métricas y dimensiones» (mockup): hint arriba, a la izquierda el catálogo agrupado (con
 * buscador), a la derecha la lista ordenable con grip y ✕ del Tablero, y el botón de aplicar.
 *
 * Sirve a los DOS scopes (card hwuk9PW7), porque es el mismo gesto sobre distinto alcance:
 *  - `sheet`: la PALETA de la hoja — qué campos entran al informe y sobre qué eligen los
 *    gráficos. Su tope es el del layout (120 campos).
 *  - `table`: las COLUMNAS de una tabla. Su tope es el del panel (40 métricas / 10 dimensiones),
 *    y lo que se elija fuera de la paleta se suma a la paleta al aplicar (lo hace el reducer).
 *
 * Tanda «Feedback de Hesta» (E1, E12, momento 2):
 *  - Los grupos se pliegan. Arranca desplegado sólo el que tiene algo elegido; la búsqueda
 *    despliega los que tienen resultados.
 *  - Dimensiones y métricas llevan su marca (rótulo + color por token, `--inf-kind-*`) en las dos
 *    columnas, y las elegidas son DOS listas: el arrastre no cruza de un tipo al otro.
 *  - En una tabla, si la selección junta GA4 con columnas que lo parten en filas aparte, lo avisa
 *    con [Quitar esas columnas] y [Dejar así] (`ga4Split.js`). En la paleta no: ahí no hay filas.
 *
 * No reusa `DataExplorerColumnsModal`: su botón dice "Aplicar" fijo y arrastra el selector de
 * sets de columnas, que en esta modalidad no existe (los sets son plantillas «solo tabla»).
 * `onApply(fields)` con `[{name, kind}]`: primero las dimensiones, después las métricas, cada
 * grupo en el orden de su lista.
 *
 * `clientHasGa4`: el cliente del informe tiene una cuenta de GA4. Decide si la búsqueda sin
 * resultados explica que un parámetro de GA4 tiene que estar registrado para aparecer.
 * `ga4SplitKept`: el usuario ya eligió [Dejar así] para esta tabla (`onKeepGa4Split` lo avisa).
 * `ga4CrossLevels`: los niveles con los que el GA4 del cliente cruza con su pauta (`useGa4CrossLevels`).
 * Con grupo o anuncio en la tabla y el cruce aplicado, el GA4 cae en la fila de cada grupo y el aviso
 * no sale (`ga4SplitColumns`). `dimensionsFilters`: los filtros del informe; con uno que el nodo no
 * admite, no cruza y el aviso vuelve.
 * `addingKind`: se abrió desde «+ Agregar otra métrica» (o dimensión) de un gráfico (C-05). Es la
 * misma paleta; la bajada dice que lo que se marque vuelve elegido en ese gráfico.
 */
export function FieldsModal({isOpen, toggle, fieldsData, sourceType, fields, onApply, scope = 'sheet', clientHasGa4 = false,
                             ga4SplitKept = false, onKeepGa4Split = null, ga4CrossLevels = [], dimensionsFilters = [],
                             addingKind = null}) {
    const {t, i18n} = useTranslation();
    const isTable = scope === 'table';
    const [active, setActive] = useState([]);
    const [search, setSearch] = useState('');
    const [openGroups, setOpenGroups] = useState(() => new Set());
    // Los grupos que el usuario plegó MIENTRAS buscaba. Van aparte de `openGroups` para que borrar
    // la búsqueda devuelva los grupos a como estaban: un clic durante la búsqueda que escribiera en
    // `openGroups` invertía el plegado al borrarla (review C-armado-05).
    const [searchCollapsed, setSearchCollapsed] = useState(() => new Set());
    const [keptNow, setKeptNow] = useState(false);

    const dimensionGroups = useMemo(() => availableFieldsByGroup(fieldsData, sourceType, 'DIMENSIONS'), [fieldsData, sourceType]);
    const metricGroups = useMemo(() => availableFieldsByGroup(fieldsData, sourceType, 'METRICS'), [fieldsData, sourceType]);

    useEffect(() => {
        if (!isOpen) return;
        const initial = (fields || []).map((f) => f.name);
        setActive(initial);
        setSearch('');
        setSearchCollapsed(new Set());
        setKeptNow(false);
        setOpenGroups(initialOpenGroups({DIMENSIONS: dimensionGroups, METRICS: metricGroups}, initial));
    }, [isOpen, fields, dimensionGroups, metricGroups]);

    const needle = search.trim().toLowerCase();
    const visibleGroups = (groups) => groups
        .map((g) => ({...g, fields: g.fields.filter((field) => fieldMatchesSearch(field, needle))}))
        .filter((g) => g.fields.length > 0);
    const visibleDimensionGroups = visibleGroups(dimensionGroups);
    const visibleMetricGroups = visibleGroups(metricGroups);
    // La búsqueda no dejó NINGÚN campo: sin esto la columna quedaba vacía, sin decir nada.
    const searchFindsNothing = !!needle && !visibleDimensionGroups.length && !visibleMetricGroups.length;

    const toggleField = (id) => setActive((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    const flip = (key) => (prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    };
    // Con una búsqueda, todo grupo con resultados arranca desplegado —plegado escondería justo lo que
    // se buscó— y el clic lo pliega sólo mientras dura la búsqueda.
    const toggleGroup = (key) => (needle ? setSearchCollapsed(flip(key)) : setOpenGroups(flip(key)));
    const isGroupOpen = (key) => (needle ? !searchCollapsed.has(key) : openGroups.has(key));
    const changeSearch = (value) => {
        setSearch(value);
        if (!value.trim()) setSearchCollapsed(new Set());
    };

    const columnData = (id) => {
        const field = fieldsData && fieldsData.FIELD_DATA_BY_ID && fieldsData.FIELD_DATA_BY_ID[id];
        // `ActiveColumnsList` pinta `title` (y opcionalmente `subtitle`/`helper`). El `helper` es la
        // descripción del catálogo, que sólo traen los personalizados de GA4 (como en el Explorador).
        const helper = field && field.ga4_custom ? field.description : undefined;
        return {title: (field && field.name) || id, id, helper};
    };
    const fieldName = (id) => columnData(id).title;
    const selectedDimensions = active.filter((id) => kindOf(fieldsData, id) === FIELD_KINDS.DIMENSION);
    const selectedMetrics = active.filter((id) => kindOf(fieldsData, id) === FIELD_KINDS.METRIC);
    const selected = [
        ...selectedDimensions.map((name) => ({name, kind: FIELD_KINDS.DIMENSION})),
        ...selectedMetrics.map((name) => ({name, kind: FIELD_KINDS.METRIC})),
    ];
    // El copy pide «al menos una métrica» y el botón se habilitaba con sólo dimensiones: la
    // hoja quedaba sin nada que medir (QA FE-22).
    const hasMetric = selectedMetrics.length > 0;
    // El tope del PANEL sólo aplica a una tabla; la paleta tiene el suyo, que es otro y más alto.
    const overflow = isTable ? fieldsOverflow(selected) : {
        ...fieldsOverflow([]), layout: layoutFieldsOverflow(selected), hasOverflow: layoutFieldsOverflow(selected).hasOverflow,
    };
    const splitColumns = isTable && !ga4SplitKept && !keptNow
        ? ga4SplitColumns(selected, fieldsData, {sourceType, levels: ga4CrossLevels, filters: dimensionsFilters})
        : [];
    const apply = () => onApply(selected);

    const removeSplitColumns = () => setActive(withoutColumns(selected, splitColumns).map((f) => f.name));
    const keepSplit = () => {
        setKeptNow(true);
        if (onKeepGa4Split) onKeepGa4Split();
    };

    // El «?» vive DENTRO del <label>: el clic en el ícono no tiene que tildar el campo, así que se
    // cancela ahí (el tooltip es de hover). Lo llevan los personalizados de GA4 (su ayuda viene en el
    // catálogo) y todo campo con texto propio en `FIELD_DESCRIPTIONS` — ahí está, por ejemplo, que
    // LinkedIn por país o región es aproximado, que conviene saberlo ANTES de elegirlo (card ctbIdOOc).
    const renderHelp = (field) => (hasFieldDescription(field.id) || (field.ga4_custom && field.description)) && (
        <span className="inf-fields-help" onClick={(e) => e.preventDefault()}>
            <FieldHelpIcon fieldId={field.id} description={field.description}/>
        </span>
    );

    // El id lleva el segmento: GA4 y Rendimiento tienen dimensiones Y métricas, y con el id del grupo
    // a secas había dos listas con el mismo id y el `aria-controls` de Métricas apuntaba a la de
    // Dimensiones (review C-armado-04).
    const listIdOf = (key) => `inf-fields-group-${key.replace(/[^A-Za-z0-9_-]/g, '-')}`;

    const renderGroupFields = (group, key) => (
        <ul className="inf-fields-list" id={listIdOf(key)}>
            {group.fields.map((field) => (
                <li key={field.id}>
                    <label className="inf-fields-item">
                        <input type="checkbox" checked={active.includes(field.id)} onChange={() => toggleField(field.id)}/>
                        <span>{field.name}</span>
                        {renderHelp(field)}
                    </label>
                </li>
            ))}
        </ul>
    );

    // `kindClass`: `-dimension` / `-metric`, el mismo modificador del rótulo y del riel de color en
    // las dos columnas.
    const renderSegment = (segmentKey, kindClass, title, visible) => {
        if (!visible.length) return null;
        return (
            <div className={`inf-fields-segment ${kindClass}`} data-testid={`segment-${segmentKey}`}>
                <h4 className="inf-fields-segment-title"><span className={`inf-kind-tag ${kindClass}`}>{title}</span></h4>
                {visible.map((group) => {
                    const key = groupKey(segmentKey, group.id);
                    // Un grupo sin nombre no tiene encabezado que apretar: va siempre abierto.
                    const open = !group.name || isGroupOpen(key);
                    const chosenCount = group.fields.filter((field) => active.includes(field.id)).length;
                    return (
                        <div key={group.id} className={`inf-fields-group ${open ? '-open' : ''}`}>
                            {group.name && (
                                <button type="button" className="inf-fields-group-toggle" aria-expanded={open}
                                        aria-controls={open ? listIdOf(key) : undefined} data-testid={`group-toggle-${key}`}
                                        onClick={() => toggleGroup(key)}>
                                    <span className="inf-fields-group-caret" aria-hidden="true">▸</span>
                                    <span className="inf-fields-group-name">{group.name}</span>
                                    {chosenCount > 0 && <span className="inf-fields-group-count">{chosenCount}</span>}
                                </button>
                            )}
                            {open && renderGroupFields(group, key)}
                            {/* Sólo están los personalizados REGISTRADOS en la propiedad: el pie dice
                                cómo sumar el que falta, sin pasar por soporte. Es <div> y no <p>: una
                                regla legacy de `_columns-selector-modal.scss` pinta gris claro e
                                itálica todo <p> de esta columna (ver `FieldSearchEmpty`). */}
                            {open && group.id === GA4_CUSTOM_GROUP && (
                                <div className="inf-fields-group-footer" data-testid="ga4-custom-footer">
                                    {t('informes.fields.ga4_custom_missing', '¿Falta alguna?')}
                                    <LinkToArticle article={HELP_GA4_CUSTOM_FIELDS} plain
                                                   text={t('informes.fields.ga4_custom_howto', 'Cómo registrarlas en GA4')}/>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderSelectedKind = (kind, kindClass, title, ids) => ids.length > 0 && (
        <div className={`inf-fields-sortable ${kindClass}`} data-testid={`selected-${kindClass.slice(1)}`}>
            <div className={`inf-kind-tag ${kindClass}`}>{title}</div>
            <ActiveColumnsList activeColumns={ids}
                               onColumnsPositionChange={(next) => setActive((prev) => reorderWithinKind(prev, fieldsData, kind, next))}
                               getColumnData={columnData} onColumnRemove={(id) => toggleField(id)}/>
        </div>
    );

    return (
        <Modal isOpen={isOpen} toggle={toggle} className="columns-selector-modal inf-fields-modal" data-testid="fields-modal">
            <ModalHeader toggle={toggle}>
                {isTable
                    ? t('informes.fields.title_table', 'Columnas de esta tabla')
                    : t('informes.fields.title', 'Métricas y dimensiones')}
            </ModalHeader>
            <ModalBody>
                <div className="modal-section inf-fields">
                    <p className="inf-modal-lead" data-testid="fields-modal-lead">
                        {isTable
                            ? t('informes.fields.hint_table', 'Qué muestra esta tabla, y en qué orden. Las demás tablas del informe no cambian. Lo que elijas y no esté en «Métricas y dimensiones» se agrega ahí solo.')
                            : addingLead(t, addingKind)}
                    </p>
                    <div className="inf-fields-columns">
                        <div className="inf-fields-available">
                            {renderSearchBar({searchQuery: search, placeholder: t('informes.fields.search', 'Buscar una métrica o dimensión…'), onSearchChange: (e) => changeSearch(e.target.value)})}
                            {renderSegment('DIMENSIONS', '-dimension', t('informes.fields.dimensions', 'Dimensiones'), visibleDimensionGroups)}
                            {renderSegment('METRICS', '-metric', t('informes.fields.metrics', 'Métricas'), visibleMetricGroups)}
                            {searchFindsNothing && <FieldSearchEmpty query={search} showGa4Hint={clientHasGa4}/>}
                        </div>
                        <div className="inf-fields-selected">
                            <h4 className="inf-fields-segment-title">
                                {isTable
                                    ? t('informes.fields.selected', {count: active.length, defaultValue: 'Seleccionadas · orden de la tabla ({{count}})'})
                                    : t('informes.fields.selected_sheet', {count: active.length, defaultValue: 'Seleccionadas ({{count}})'})}
                            </h4>
                            {/* El arrastre ya existía y nadie lo encontraba (E12): la pista lo dice,
                                y dice también que cada tipo se ordena dentro de su lista. Sólo con
                                algo que ordenar: una dimensión y una métrica no se cambian de lugar. */}
                            {(selectedDimensions.length > 1 || selectedMetrics.length > 1) && (
                                <div className="inf-fields-drag-hint" data-testid="drag-hint">
                                    {t('informes.fields_modal.drag_hint', 'Arrastra para cambiar el orden. Las dimensiones van primero y las métricas después.')}
                                </div>
                            )}
                            {renderSelectedKind(FIELD_KINDS.DIMENSION, '-dimension', t('informes.fields.dimensions', 'Dimensiones'), selectedDimensions)}
                            {renderSelectedKind(FIELD_KINDS.METRIC, '-metric', t('informes.fields.metrics', 'Métricas'), selectedMetrics)}
                            {!hasMetric && <p className="inf-fields-empty">{t('informes.fields.none', 'Elige al menos una métrica.')}</p>}
                        </div>
                    </div>
                    {/* El tope lo aplica el backend sobre la tabla, que lleva TODO lo seleccionado.
                        El aviso va FUERA de la columna «Seleccionadas», que es un scroller: adentro
                        quedaba debajo del pliegue justo cuando más campos hay —con 53 había que
                        bajar 2153 px—, o sea invisible en el único caso que existe para atajar
                        (QA-informes-05 / QA-VIS-informes-03). Acá queda pegado al footer, que es
                        donde el usuario mira cuando decide aplicar. El de GA4 va en la misma barra
                        por la misma razón. */}
                    {(overflow.hasOverflow || splitColumns.length > 0) && (
                        <div className="inf-fields-over-bar">
                            {splitColumns.length > 0 && (
                                <div className="inf-ga4-split" role="status" data-testid="ga4-split-notice">
                                    <div className="inf-ga4-split-title">{t('informes.ga4_split.title', 'GA4 va a aparecer en filas aparte')}</div>
                                    <div className="inf-ga4-split-body">
                                        {t('informes.ga4_split.body', {
                                            columns: listOfNames(splitColumns.map(fieldName), i18n && i18n.language),
                                            interpolation: {escapeValue: false},
                                            defaultValue: 'GA4 se reparte por campaña. Con {{columns}} en la tabla, sus ventas aparecen en filas propias y no junto a cada campaña.',
                                        })}
                                    </div>
                                    <div className="inf-ga4-split-actions">
                                        <button type="button" className="inf-ga4-split-remove" onClick={removeSplitColumns} data-testid="ga4-split-remove">
                                            {t('informes.ga4_split.remove', 'Quitar esas columnas')}
                                        </button>
                                        <button type="button" className="inf-ga4-split-keep" onClick={keepSplit} data-testid="ga4-split-keep">
                                            {t('informes.ga4_split.keep', 'Dejar así')}
                                        </button>
                                    </div>
                                </div>
                            )}
                            {overflow.metrics.over && (
                                <p className="inf-fields-over" data-testid="metrics-over">
                                    {t('informes.fields.metrics_over', {count: overflow.metrics.count - overflow.metrics.max, max: overflow.metrics.max, defaultValue: 'Hay {{count}} métricas de más: un panel admite hasta {{max}}. Quita las que sobren o el informe no se va a poder calcular.'})}
                                </p>
                            )}
                            {overflow.dimensions.over && (
                                <p className="inf-fields-over" data-testid="dimensions-over">
                                    {t('informes.fields.dimensions_over', {count: overflow.dimensions.count - overflow.dimensions.max, max: overflow.dimensions.max, defaultValue: 'Hay {{count}} dimensiones de más: un panel admite hasta {{max}}. Quita las que sobren o el informe no se va a poder calcular.'})}
                                </p>
                            )}
                            {overflow.layout && overflow.layout.hasOverflow && (
                                <p className="inf-fields-over" data-testid="layout-over">
                                    {t('informes.fields.layout_over', {count: overflow.layout.count - overflow.layout.max, max: overflow.layout.max, defaultValue: 'Hay {{count}} campos de más: un informe admite hasta {{max}} entre métricas y dimensiones. Quita los que sobren o el informe no se va a poder calcular.'})}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                {/* `-alt -negative`, como el «Cancelar» de los otros modales de Informes: con `-negative`
                    a secas quedaba de otra familia que «Aplicar» (34 px, mayúsculas; QA-VIS-aviso-01). */}
                <button type="button" className="btn -alt -negative" onClick={toggle}>{t('common.cancel', 'Cancelar')}</button>
                <button type="button" className="btn -alt" onClick={apply} disabled={!hasMetric} data-testid="apply-fields">
                    {isTable
                        ? t('informes.fields.apply_table', 'Aplicar a esta tabla')
                        : t('informes.fields.apply', 'Aplicar a la plantilla')}
                </button>
            </ModalFooter>
        </Modal>
    );
}
